import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AtSign, CheckCircle2, EyeOff, Globe, KeyRound, Loader2, LockKeyhole, MailCheck, Phone, Save, ServerCog, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PublicAgencyProfileEditor } from "@/components/agency/PublicAgencyProfileEditor";

const initialSmtp = {
    provider: "gmail", host: "", port: 465, username: "", password: "", from_email: "", from_name: "", encryption: "ssl",
};

const SMTP_PROVIDERS = [
    ["gmail", "Gmail"], ["outlook", "Outlook / Hotmail"], ["yahoo", "Yahoo"],
    ["libero", "Libero"], ["aruba", "Aruba"], ["tim", "TIM / Alice"],
    ["virgilio", "Virgilio"], ["tiscali", "Tiscali"], ["custom", "Personalizzato"],
];

const accountLabel = (user) => {
    if (user?.role !== "agenzia") return "Account privato";
    if (user?.plan_id === "agenzia") return "Agenzia Full";
    if (user?.plan_id === "premium") return "Agenzia Pro";
    return "Account agenzia";
};

export const ProfileSettings = ({ user, onContactsPublic, onRefresh }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
    const [emailBusy, setEmailBusy] = useState(false);
    const [smtpForm, setSmtpForm] = useState(initialSmtp);
    const [smtpBusy, setSmtpBusy] = useState(false);
    const [phone, setPhone] = useState(user?.phone || "");
    const [phoneBusy, setPhoneBusy] = useState(false);
    const isPublic = !!user?.contacts_public;

    const loadSettings = useCallback(async () => {
        try {
            const { data } = await api.get("/profile/settings");
            setSettings(data);
            setPhone(data.account?.phone || user?.phone || "");
            if (data.smtp?.available) {
                setSmtpForm((current) => ({
                    ...current,
                    provider: data.smtp.provider || (data.smtp.configured ? "custom" : "gmail"),
                    host: data.smtp.host || "",
                    port: data.smtp.port || 465,
                    username: data.smtp.username || "",
                    from_email: data.smtp.from_email || data.account?.email || "",
                    from_name: data.smtp.from_name || "",
                    encryption: data.smtp.encryption || "ssl",
                    password: "",
                }));
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Impossibile caricare le impostazioni profilo");
        } finally {
            setLoading(false);
        }
    }, [user?.phone]);

    useEffect(() => { loadSettings(); }, [loadSettings]);
    useEffect(() => {
        const result = new URLSearchParams(window.location.search).get("email_change");
        if (result === "verified") toast.success("Nuova email verificata e attivata");
        if (result === "invalid") toast.error("Link di verifica non valido o scaduto");
        if (result === "conflict") toast.error("La nuova email è già utilizzata da un altro account");
    }, []);

    const requestEmailChange = async (event) => {
        event.preventDefault();
        setEmailBusy(true);
        try {
            const { data } = await api.post("/profile/email-change/request", emailForm);
            toast.success(`Link di verifica inviato a ${data.pending_email}`);
            setEmailForm({ new_email: "", current_password: "" });
            await loadSettings();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Richiesta di cambio email non riuscita");
        } finally {
            setEmailBusy(false);
        }
    };

    const saveSmtp = async (event) => {
        event.preventDefault();
        setSmtpBusy(true);
        try {
            const { data } = await api.put("/profile/smtp", { ...smtpForm, port: Number(smtpForm.port) });
            setSettings((current) => ({ ...current, smtp: data.smtp }));
            setSmtpForm((current) => ({ ...current, password: "" }));
            if (data.test_ok) toast.success(data.test_detail);
            else toast.error(data.test_detail || "Configurazione salvata, ma email di prova non inviata");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Salvataggio SMTP non riuscito");
        } finally {
            setSmtpBusy(false);
        }
    };

    const savePhone = async (event) => {
        event.preventDefault();
        setPhoneBusy(true);
        try {
            await api.put("/auth/profile", { phone });
            toast.success("Numero di telefono salvato");
            await onRefresh?.();
            await loadSettings();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Salvataggio numero non riuscito");
        } finally {
            setPhoneBusy(false);
        }
    };

    if (loading) {
        return <div id="my-profile-section" data-testid="profile-settings-loading" className="profile-settings-loading"><Loader2 className="animate-spin" /> Caricamento impostazioni...</div>;
    }

    const account = settings?.account || {};
    const smtp = settings?.smtp || {};
    return (
        <section id="my-profile-section" data-testid="my-profile-section" data-profile-settings="true" className="profile-settings-shell mb-20 scroll-mt-28">
            <header className="profile-settings-header">
                <div>
                    <span className="label-eyebrow">Centro account</span>
                    <h1 className="font-serif text-4xl sm:text-5xl text-ink-50 mt-2">Il mio profilo</h1>
                    <p className="text-sm text-ink-400 mt-3 max-w-2xl">Gestisci accesso, privacy{user?.plan_id === "premium" ? ", profilo pubblico" : ""} e canali email da un unico spazio protetto.</p>
                </div>
                <div data-testid="profile-account-tier" className="profile-tier-badge"><ShieldCheck size={16} /> {accountLabel(user)}</div>
            </header>

            <div className="profile-settings-grid">
                <div className="profile-neu-card" data-testid="profile-access-card">
                    <CardTitle icon={<KeyRound />} eyebrow="Accesso" title="Email di accesso" />
                    <div className="profile-current-email" data-testid="profile-current-email">
                        <AtSign size={16} /><span>{account.email || user?.email}</span><span className="profile-verified-pill"><CheckCircle2 size={12} /> Verificata</span>
                    </div>
                    {account.can_change_email ? (
                        <form onSubmit={requestEmailChange} className="space-y-4 mt-6" data-testid="email-change-form">
                            <ProfileField label="Nuova email">
                                <Input data-testid="email-change-new-email-input" type="email" required autoComplete="email" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} className="profile-input" placeholder="nuova@email.it" />
                            </ProfileField>
                            <ProfileField label="Password attuale">
                                <Input data-testid="email-change-password-input" type="password" required autoComplete="current-password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} className="profile-input" placeholder="••••••••" />
                            </ProfileField>
                            <Button data-testid="email-change-submit-button" type="submit" disabled={emailBusy} className="profile-primary-button">
                                {emailBusy ? <Loader2 className="animate-spin" /> : <MailCheck />} Richiedi verifica
                            </Button>
                        </form>
                    ) : (
                        <div data-testid="email-change-password-required" className="profile-inset-note mt-6">
                            <LockKeyhole size={19} />
                            <div><strong>Password non ancora impostata</strong><p>Usa “Password dimenticata” nella pagina di accesso per impostarla e abilitare la modifica email.</p></div>
                        </div>
                    )}
                    {account.pending_email && (
                        <div data-testid="email-change-pending-status" className="profile-pending-note mt-4">
                            <MailCheck size={16} /><span>Verifica in attesa per <strong>{account.pending_email}</strong></span>
                        </div>
                    )}
                </div>

                <div className="profile-neu-card" data-testid="profile-privacy-card">
                    <CardTitle icon={<ShieldCheck />} eyebrow="Privacy annunci" title="Visibilità contatti" />
                    <p className="text-sm text-ink-400 mt-3">Decidi se telefono ed email sono subito visibili oppure disponibili solo dopo il tuo consenso.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                        <PrivacyChoice testid="contacts-public-privato" active={!isPublic} icon={<EyeOff />} title="Su richiesta" text="Contatti protetti fino alla tua approvazione." onClick={async () => { await onContactsPublic(false); await onRefresh?.(); }} />
                        <PrivacyChoice testid="contacts-public-pubblico" active={isPublic} icon={<Globe />} title="Pubblici" text="Contatti visibili subito negli annunci." onClick={async () => { await onContactsPublic(true); await onRefresh?.(); }} />
                    </div>
                    <div data-testid="contacts-visibility-status" className="profile-status-line mt-5">
                        <span className={isPublic ? "profile-dot profile-dot-public" : "profile-dot"} /> Stato: <strong>{isPublic ? "Pubblico" : "Protetto"}</strong>
                    </div>
                </div>

                <form onSubmit={savePhone} className="profile-neu-card" data-testid="profile-phone-form">
                    <CardTitle icon={<Phone />} eyebrow="Contatti" title="Numero di telefono" />
                    <p className="text-sm text-ink-400 mt-3">Viene proposto automaticamente quando pubblichi un nuovo annuncio e resta modificabile per ogni immobile.</p>
                    <ProfileField label="Numero di telefono">
                        <Input data-testid="profile-phone-input" type="tel" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value)} className="profile-input mt-5" placeholder="+39 333 123 4567" />
                    </ProfileField>
                    <Button data-testid="profile-phone-save-button" type="submit" disabled={phoneBusy} className="profile-primary-button mt-4">
                        {phoneBusy ? <Loader2 className="animate-spin" /> : <Save />} Salva numero
                    </Button>
                </form>
            </div>

            {user?.role === "agenzia" && user?.plan_id === "premium" && (
                <PublicAgencyProfileEditor user={user} onSaved={onRefresh} testPrefix="pro-public-profile" className="profile-neu-card mt-6" />
            )}

            {smtp.available && (
                <form onSubmit={saveSmtp} className="profile-neu-card profile-smtp-card mt-6" data-testid="smtp-settings-form">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <CardTitle icon={<ServerCog />} eyebrow="Agenzia Pro / Full" title="Server SMTP" />
                        <div data-testid="smtp-config-status" className={`profile-tier-badge ${smtp.configured ? "profile-tier-ok" : ""}`}>
                            {smtp.configured ? <CheckCircle2 size={15} /> : <LockKeyhole size={15} />}{smtp.configured ? "Configurato" : "Da configurare"}
                        </div>
                    </div>
                    <p className="text-sm text-ink-400 mt-3 max-w-3xl">Scegli il provider: configuriamo automaticamente il server. La password viene cifrata e non viene mai restituita al browser.</p>
                    <div className="profile-smtp-grid mt-7">
                        <ProfileField label="Provider email">
                            <select data-testid="smtp-provider-select" value={smtpForm.provider} onChange={(e) => setSmtpForm({ ...smtpForm, provider: e.target.value })} className="profile-input profile-select">
                                {SMTP_PROVIDERS.map(([value, label]) => <option data-testid={`smtp-provider-option-${value}`} key={value} value={value}>{label}</option>)}
                            </select>
                        </ProfileField>
                        <ProfileField label="Email mittente"><Input data-testid="smtp-from-email-input" required type="email" value={smtpForm.from_email} onChange={(e) => setSmtpForm({ ...smtpForm, from_email: e.target.value })} className="profile-input" placeholder="agenzia@example.it" /></ProfileField>
                        <ProfileField label="Password / password per app"><Input data-testid="smtp-password-input" type="password" required={!smtp.configured} autoComplete="new-password" value={smtpForm.password} onChange={(e) => setSmtpForm({ ...smtpForm, password: e.target.value })} className="profile-input" placeholder={smtp.configured ? "Lascia vuoto per mantenerla" : "Password SMTP"} /></ProfileField>
                        <ProfileField label="Nome mittente"><Input data-testid="smtp-from-name-input" required value={smtpForm.from_name} onChange={(e) => setSmtpForm({ ...smtpForm, from_name: e.target.value })} className="profile-input" placeholder="es. Immobiliare Rossi" /></ProfileField>
                    </div>
                    {smtpForm.provider === "custom" && (
                        <div data-testid="smtp-custom-fields" className="profile-smtp-technical mt-6">
                            <span className="label-eyebrow">Configurazione personalizzata</span>
                            <div className="profile-smtp-grid mt-4">
                                <ProfileField label="Host SMTP"><Input data-testid="smtp-host-input" required value={smtpForm.host} onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })} className="profile-input" placeholder="smtp.example.it" /></ProfileField>
                                <ProfileField label="Porta"><Input data-testid="smtp-port-input" required type="number" min="1" max="65535" value={smtpForm.port} onChange={(e) => setSmtpForm({ ...smtpForm, port: e.target.value })} className="profile-input" /></ProfileField>
                                <ProfileField label="Cifratura"><select data-testid="smtp-encryption-select" value={smtpForm.encryption} onChange={(e) => setSmtpForm({ ...smtpForm, encryption: e.target.value })} className="profile-input profile-select"><option value="ssl">SSL/TLS</option><option value="starttls">STARTTLS</option></select></ProfileField>
                                <ProfileField label="Username SMTP"><Input data-testid="smtp-username-input" required autoComplete="username" value={smtpForm.username} onChange={(e) => setSmtpForm({ ...smtpForm, username: e.target.value })} className="profile-input" placeholder="utente@example.it" /></ProfileField>
                            </div>
                        </div>
                    )}
                    <div data-testid="smtp-auto-test-info" className="profile-provider-note mt-5"><MailCheck size={16} /> Dopo il salvataggio invieremo automaticamente un’email di prova a <strong>{account.email || user?.email}</strong>.</div>
                    <div className="flex flex-wrap gap-3 mt-7">
                        <Button data-testid="smtp-save-button" type="submit" disabled={smtpBusy} className="profile-primary-button">{smtpBusy ? <Loader2 className="animate-spin" /> : <ShieldCheck />} Salva cifrato</Button>
                    </div>
                </form>
            )}
        </section>
    );
};

const CardTitle = ({ icon, eyebrow, title }) => <div className="flex items-center gap-3"><span className="profile-icon-well">{icon}</span><div><span className="label-eyebrow">{eyebrow}</span><h3 className="font-serif text-xl text-ink-50 mt-1">{title}</h3></div></div>;
const ProfileField = ({ label, children }) => <label className="block text-xs text-ink-400"><span className="block mb-2 uppercase tracking-wider">{label}</span>{children}</label>;
const PrivacyChoice = ({ testid, active, icon, title, text, onClick }) => (
    <button data-testid={testid} type="button" onClick={onClick} className={`profile-choice ${active ? "profile-choice-active" : ""}`}>
        <span className="profile-choice-icon">{icon}</span><span><strong>{title}</strong><small>{text}</small></span>{active && <CheckCircle2 className="ml-auto" size={16} />}
    </button>
);